import domready from 'domready';
import React, { Suspense } from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import isElectron from 'is-electron';
import { createIntl } from 'react-intl';
import { IntlProvider } from 'react-intl-redux';
import { getUA } from 'react-device-detect';
import { Route, HashRouter, BrowserRouter, Switch } from 'react-router-dom';

import randomString from 'random-string';
import Logger from './Logger';
import debug from 'debug';
import RoomClient from './RoomClient';
import RoomContext from './RoomContext';
import deviceInfo from './deviceInfo';

import * as roomActions from './actions/roomActions';
import * as meActions from './actions/meActions';
import * as settingsActions from './actions/settingsActions';

import UnsupportedBrowser from './components/UnsupportedBrowser';
import LoadingView from './components/LoadingView';
import { MuiThemeProvider, createTheme } from '@material-ui/core/styles';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { persistor, store } from './store';
import { SnackbarProvider } from 'notistack';
import * as serviceWorker from './serviceWorker';
import { ReactLazyPreload } from './components/ReactLazyPreload';
import { detectDevice } from 'mediasoup-client';

import LoginDialog from './components/LoginDialog';
import ZYLoginDialog from './components/ZYLoginDialog';

import ChooseRoom from './components/ChooseRoom';

import { wrapperAxios } from './utils';

import './index.css';
import Stats from './components/Stats';

const logger = new Logger('index');

const App = ReactLazyPreload(() => import(/* webpackChunkName: "app" */ './components/App'));

const supportedBrowsers = {
	'windows' : {
		'internet explorer' : '>12',
		'microsoft edge'    : '>18'
	},
	'safari'                       : '>=13',
	'firefox'                      : '>=60',
	'chrome'                       : '>=74',
	'chromium'                     : '>=74',
	'opera'                        : '>=62',
	'samsung internet for android' : '>=11.1.1.52'
};

logger.debug('当前环境变量: %o %o', process.env, getUA);

const intl = createIntl({ locale: 'zh-cn' });

if (window.config.ReactAppDebug) {
	//debug.enable('mediasoup-client*,huijian*,-engine*,-socket*,-RIE*,*INFO*,*WARN*,*ERROR*');
	//debug.enable(process.env.REACT_APP_DEBUG);
	debug.enable(window.config.ReactAppDebug);
}

let roomClient;
const theme = createTheme(window.config.theme);
let Router;

if (isElectron()) {
	Router = HashRouter;
}
else {
	Router = BrowserRouter;
}

domready(async () => {

	logger.debug('DOM ready');

	await run();
});

async function run()
{
	logger.debug('run() [environment:%s]', process.env.NODE_ENV);

	const peerId = randomString({ length: 8 }).toLowerCase();
	const { pathname } = window.location;
	let basePath = pathname.substring(0, pathname.lastIndexOf('/'));

	if (!basePath) {

		basePath = '/';
	}
	// Get current device.
	const device = deviceInfo();
	let unsupportedBrowser = false;
	let webrtcUnavailable = false;

	// 返回支持的环境
	if (detectDevice() === undefined) {

		logger.warn('Your browser is not supported [deviceInfo:"%o"]', device);

		unsupportedBrowser = true;
	}  else if (
		navigator.mediaDevices === undefined ||
		navigator.mediaDevices.getUserMedia === undefined ||
		window.RTCPeerConnection === undefined
	) {

		logger.warn('Your browser is not supported [deviceInfo:"%o"]', device);

		webrtcUnavailable = true;
	} else if (
		// 检测当前的浏览器是否支持(satisfies)
		!device.browser.satisfies(
			
			window.config.supportedBrowsers || supportedBrowsers
		)
	) {
		logger.warn('Your browser is not supported [deviceInfo:"%o"]', device);  // 不支持

		unsupportedBrowser = true;
	}
	else {
		
		logger.debug('Your browser is supported [deviceInfo:"%o"]', device);
	}

	logger.debug(`unsupportedBrowser:${unsupportedBrowser} webrtcUnavailable:${webrtcUnavailable}`);

	if (unsupportedBrowser || webrtcUnavailable) {

		render(
			<Provider store={store}>
				<MuiThemeProvider theme={theme}>
					<IntlProvider value={intl}>
						<UnsupportedBrowser
							webrtcUnavailable={webrtcUnavailable}
							platform={device.platform}
						/>
					</IntlProvider>
				</MuiThemeProvider>
			</Provider>,
			document.getElementById('huijian')
		);

		return;
	}

	RoomClient.init({ store, intl });

	const _data = {};
	const _loginTypeData = await wrapperAxios.post('/user/getLoginType', _data);

	if (_loginTypeData.isSuccess && _loginTypeData.code === '200') {

		const _loginType =  _loginTypeData.data && _loginTypeData.data.loginType === '4' ? 'zy' : 'hj';

		logger.debug(`当前登录类型: loginType ${_loginType}`);

		store.dispatch(
			meActions.setLoginType(_loginType)
		);
	}

	if (window.config.delayDisplayToolbars) {

		store.dispatch(
			roomActions.setToolbarsVisible(false)
		);
	}

	store.dispatch(
		meActions.setMe({
			peerId,
			loginEnabled : window.config.loginEnabled
		})
	);

	//store.dispatch(settingsActions.setVideoResolution('high')); // low | high

	const urlParser = new URL(window.location);
	const parameters = urlParser.searchParams;

	// 入会公共参数
	const accessCode = null;
	const produce = true;
	const forceTcp = false;
	const muted = false;

    // 自动入会公共参数 默认视频，音频关闭。
	let videoMute = parameters.get('videoMute') || 'true';
	let audioMute = parameters.get('audioMute') || 'true';

	// 公共参数
	const confPwd = parameters.get('confPwd');
	const lang = parameters.get('lang');    // 连接自动入会时指定的语言环境

	// 邀请码自动入会参数
	let inviteCode = parameters.get('code');
	let displayName = parameters.get('displayName');

	// token 自动入会参数
	const token = parameters.get('token');
	const confId = parameters.get('confId');

	// 可让第三方应用，指定视频弹出窗口在那个iframe显示
	const targetName = parameters.get('targetName');
	if (targetName) {

		store.dispatch(roomActions.setWindowOpenName(targetName));
	}

	// 账号、密码登录
	const username = parameters.get('username');
	const password = parameters.get('password');

	/**
	 * autoJoin 登录状态变量
	 * -1: 非自动登录
	 *  0: 自动登录中
	 *  1: 自动登录成功
	 *
	 * joinType
	 * 0: 邀请码
	 * 1: 账号/密码
	 * 2: token
	 */
	const _userInfo = store.getState().me.userInfo;
	if (!_userInfo) {

		store.dispatch(meActions.setUserInfo({ joinType: 1, confId, autoJoin: -1 }));
	} else {

		store.dispatch(meActions.setUserInfo({ ..._userInfo, confId, autoJoin: -1 }));
	}

	let _autoJoin = -1;
	if (inviteCode !== null) { // 邀请码自动入会

		_autoJoin = 0;
		store.dispatch(settingsActions.setInviteCode(inviteCode));
		if (!displayName) {

			displayName = `Guest_${Math.floor(Math.random() * (100000 - 10000)) + 10000}`;
		}
		store.dispatch(settingsActions.setDisplayName(displayName));

		// 链接地址带 code 参数尝试自动入会
		store.dispatch(meActions.setLoggedIn(false)); // 重置登录状态
		store.dispatch(meActions.setUserInfo({ joinType: 0, code: inviteCode, displayName, confPwd, autoJoin: _autoJoin }));

	} else if (token !== null) { // token 入会

		// 链接地址带 token、confId 参数尝试自动入会
		// _autoJoin = confId ? 0 : -1;
		_autoJoin = 0;
		store.dispatch(meActions.setLoggedIn(false)); // 重置登录状态
		store.dispatch(meActions.setUserInfo({ joinType: 2, token, confId, confPwd, autoJoin: _autoJoin }));

	} else if (username !== null) { // 账号/密码入会

		// 链接地址中带 username、password、confId 参数尝试自动入会
		_autoJoin = password ? 0 : -1;
		store.dispatch(meActions.setLoggedIn(false)); // 重置登录状态
		store.dispatch(meActions.setUserInfo({ joinType: 1, username, password, confId, confPwd, autoJoin: _autoJoin }));
	}

	if (_autoJoin === 0) { // 如果为自动登录中
		// 入会视频和音频是否关闭 (默认开启)
		let mediaPerms = { video: true, audio: true };
		if (videoMute === 'true') {

			mediaPerms.video = false;
		}

		if (audioMute === 'true') {

			mediaPerms.audio = false;
		}
		store.dispatch(settingsActions.setMediaPerms(mediaPerms));
	}

	roomClient = new RoomClient({
		peerId,
		accessCode,
		device,
		produce,
		forceTcp,
		displayName,
		muted,
		basePath
	});

	roomClient.setLocale(lang);

	if (window.config.debugEnable) {

		global.CLIENT = roomClient;
	}

	global.SubDialogWins = [];

	const _loginType = store.getState().me.loginType;

	render(
		<Provider store={store}>
			<MuiThemeProvider theme={theme}>
				<IntlProvider value={intl}>
					<PersistGate loading={<LoadingView />} persistor={persistor}>
						<RoomContext.Provider value={roomClient}>
							<SnackbarProvider>
								<Router basename={basePath}>
									<Suspense fallback={<LoadingView />}>
										<React.Fragment>
											<Switch>
												<Route exact path='/' component={App} /> {/** 自动登录入口 */}
												<Route path='/choose' component={ChooseRoom} />
												<Route path='/login' component={_loginType === 'zy' ? ZYLoginDialog : LoginDialog} />
												<Route exact path='/stats' component={Stats} />
												<Route path='/:id' component={App} /> {/** 路由到指定组件 */}
											</Switch>
										</React.Fragment>
									</Suspense>
								</Router>
							</SnackbarProvider>
						</RoomContext.Provider>
					</PersistGate>
				</IntlProvider>
			</MuiThemeProvider>
		</Provider>,
		document.getElementById('huijian')
	);
}

serviceWorker.unregister();
